import React from 'react';
import { Typography, Checkbox, Box } from '@mui/material';
import CustomAccordion from '../ui/CustomAccordion';
import DynamicTable, { DynamicColumn } from '../ui/DynamicTable';

interface SyncedFamilyHistory {
  conditionId?: number;
  familyMemberId?: number;
  value: string;
  codingSystem: string;
  synced: boolean;
}

interface FamilyHistoryInCDPProps {
  selectedItems: Set<string>;
  onCheckboxChange: (itemId: string, itemType: string) => void;
  showSyncColumn?: boolean;
  syncedData?: SyncedFamilyHistory[];
  fieldConfigs?: Array<{ key: string; label: string }>;
  headerText?: string;
  syncText?: string;
}

const FamilyHistoryInCDPSection: React.FC<FamilyHistoryInCDPProps> = ({
  selectedItems,
  onCheckboxChange,
  showSyncColumn = true,
  syncedData,
  fieldConfigs,
  headerText,
  syncText,
}) => {
  const [expanded, setExpanded] = React.useState(true);

  if (!syncedData || syncedData.length === 0) return null;

  // Default columns if not provided by Sitecore
  const defaultColumns = [
    { key: 'conditionId', label: 'Code' },
    { key: 'familyMemberId', label: 'Family Member ID' },
    { key: 'value', label: 'Value' },
    { key: 'codingSystem', label: 'Coding System' },
  ];

  const parseFieldConfigs = (): Array<{ key: string; label: string }> => {
    if (!fieldConfigs || !Array.isArray(fieldConfigs)) return defaultColumns;

    return fieldConfigs
      .map((config: any) => ({
        key: config?.fields?.ExpandedViewItemKey?.value || '',
        label: config?.fields?.ExpandedViewItemLabel?.value || '',
      }))
      .filter((item) => item.key && item.label);
  };

  const configuredColumns = parseFieldConfigs();

  // Build columns dynamically based on configuration
  const columns: DynamicColumn<SyncedFamilyHistory>[] = [
    ...configuredColumns.map((config) => {
      return {
        id: config.key,
        label: config.label,
        accessor: config.key as keyof SyncedFamilyHistory,
        render: (row: SyncedFamilyHistory) => (
          <Typography sx={{ fontSize: '12px' }}>{(row as any)[config.key] || '-'}</Typography>
        ),
      };
    }),
    ...(showSyncColumn
      ? [
          {
            id: 'sync',
            label: syncText || 'Sync',
            accessor: 'synced' as keyof SyncedFamilyHistory,
            render: (row: SyncedFamilyHistory) => (
              <Box sx={{ textAlign: 'left' }}>
                {row.synced ? (
                  <Typography
                    variant="caption"
                    sx={{ fontSize: '11px', color: '#4caf50', fontWeight: 500 }}
                  >
                    Synced
                  </Typography>
                ) : (
                  <Checkbox
                    checked={selectedItems.has(
                      `familyHistory-${row.conditionId || ''}-${row.familyMemberId || ''}`
                    )}
                    onChange={() =>
                      onCheckboxChange(
                        `${row.conditionId || ''}-${row.familyMemberId || ''}`,
                        'familyHistory'
                      )
                    }
                    size="small"
                    sx={{
                      color: '#255E5E',
                      '&.Mui-checked': {
                        color: '#255E5E',
                      },
                    }}
                  />
                )}
              </Box>
            ),
          },
        ]
      : []),
  ];

  return (
    <CustomAccordion
      title={`${headerText || 'Family History in CDP'} (${syncedData.length})`}
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      sx={{
        mt: 1.5,
        '& .MuiAccordionSummary-root': {
          minHeight: '40px',
          py: 0.5,
          backgroundColor: '#255E5E',
          color: 'white',
          borderRadius: '4px 4px 0 0',
        },
        '& .MuiAccordionSummary-root.Mui-expanded': {
          minHeight: '40px',
          py: 0.5,
        },
        '& .MuiAccordionSummary-content': {
          margin: '0 !important',
          color: 'white !important',
        },
        '& .MuiAccordionSummary-content.Mui-expanded': {
          margin: '0 !important',
        },
        '&:before': { display: 'none' },
        '& .MuiAccordionDetails-root': {
          p: 0,
          border: '2px solid #255E5E',
          borderTop: 'none',
        },
      }}
      summarySx={{
        backgroundColor: '#255E5E',
        color: 'white',
        '& .MuiTypography-root': {
          color: 'white',
          fontSize: '15px',
          fontWeight: 400,
        },
        '& .MuiSvgIcon-root': {
          color: 'white',
        },
      }}
      detailsSx={{
        p: 0,
        border: '2px solid #255E5E',
        borderTop: 'none',
      }}
    >
      <DynamicTable
        columns={columns}
        data={syncedData}
        variant="compact"
        noDataMessage="No family history found"
      />
    </CustomAccordion>
  );
};

export default FamilyHistoryInCDPSection;
